import { EmptyState, Link, Table } from '@backstage/core-components';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  TechDebtIssue,
  TechDebtIssuesResponse,
} from '@internal/backstage-plugin-techdebt-common';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import {
  Button,
  Chip,
  createStyles,
  makeStyles,
  SvgIcon,
  Theme,
  Typography,
} from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { TechDebtRegistrationDrawer } from './TechDebtRegistrationDrawer';
import { techDebtApiRef } from '../api';
import { PandoraBlack, PandoraBlue, PandoraGreen, PandoraGrey } from '@internal/plugin-pandora-react';

export const techDebtEntityTabPath = '/tech-debt';
export const techDebtEntityTabTitle = 'Tech Debt';

const WSJF_MIN = 0.6;
const WSJF_MAX = 15;

const formatDate = (value: string | undefined): string => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleDateString('en-GB');
};

const formatNumber = (value: number | undefined): string => {
  if (value === undefined || Number.isNaN(value)) {
    return '-';
  }

  return String(value);
};

const formatStatus = (status: TechDebtIssue['status'] | undefined): any => {
  if (!status) {
    return '-';
  }

  let bgColor = PandoraGrey;
  let textColor = PandoraBlack;
  switch (status) {
    case 'Create':
    case 'Triaged':
      bgColor = PandoraGrey;
      break;
    case 'Ready':
    case 'Planned':
      bgColor = PandoraBlue;
      break;
    case 'Done':
      bgColor = PandoraGreen;
      break;
    case 'Cancelled/Not Tech Debt':
      bgColor = PandoraBlack;
      textColor = 'white';
      break;
  }
     return <Chip style={{ backgroundColor: bgColor, color: textColor }} label={status} />;

}


const getWsjfDisplay = (
  score: number | undefined,
): { label: string; color: string | undefined } => {
  if (score === undefined || Number.isNaN(score)) {
    return { label: '-', color: undefined };
  }

  const clamped = Math.min(WSJF_MAX, Math.max(WSJF_MIN, score));
  const ratio = (clamped - WSJF_MIN) / (WSJF_MAX - WSJF_MIN);
  const hue = Math.round((1 - ratio) * 120);

  return {
    label: clamped.toFixed(1),
    color: `hsl(${hue}, 80%, 35%)`,
  };
};

const TechDebtEntityContent = ({
  context,
  showRegisterButton = true,
}: {
  context: string;
  showRegisterButton?: boolean;
}) => {
  const { entity } = useEntity();
  const techDebtApi = useApi(techDebtApiRef);
  const inferredEntityRef = useMemo(() => stringifyEntityRef(entity), [entity]);

  const [isOpen, setIsOpen] = useState(false);
  const [issues, setIssues] = useState<TechDebtIssue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);

  const jiraIcon = <SvgIcon style={{ width: "12px", height: "12px" }}><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" id="Jira-Software-Logo-2--Streamline-Logos" height="24" width="24">
    <path stroke="#000000" stroke-linecap="round" stroke-linejoin="round" d="M17 8a1 1 0 0 0 -1 -1H6.5l0.01 0.066a4 4 0 0 0 3.96 3.434h3.03v3.03a4 4 0 0 0 3.434 3.96l0.066 0.01V8Z" stroke-width="1"></path>
    <path stroke="#000000" stroke-linecap="round" stroke-linejoin="round" d="M11.5 13.5a1 1 0 0 0 -1 -1h-9l0.01 0.066A4 4 0 0 0 5.47 16H8v2.53a4 4 0 0 0 3.434 3.96l0.066 0.01v-9Z" stroke-width="1"></path>
    <path stroke="#000000" stroke-linecap="round" stroke-linejoin="round" d="M22.5 2.5a1 1 0 0 0 -1 -1h-10l0.01 0.066A4 4 0 0 0 15.47 5H19v3.53a4 4 0 0 0 3.434 3.96l0.066 0.01v-10Z" stroke-width="1"></path>
  </svg></SvgIcon>;

  const columns = useMemo(
    () => [
      {
        title: 'Title',
        field: 'title',
        type: 'string' as const,
        render: (row: TechDebtIssue) => {
          return <Link to={row.jiraUrl || '#'} target='_blank'>{jiraIcon} {row.title}</Link>;
        },
      },
      {
        title: 'CreatedOn',
        field: 'createdAt',
        type: 'string' as const,
        render: (row: TechDebtIssue) => formatDate(row.createdAt),
      },
      {
        title: 'Status',
        field: 'status',
        type: 'string' as const,
        render: (row: TechDebtIssue) => formatStatus(row.status),
      },
      {
        title: 'Score',
        field: 'wsjfScore',
        type: 'numeric' as const,
        render: (row: TechDebtIssue) => {
          const wsjf = getWsjfDisplay(row.wsjfScore);
          return <strong style={{ color: wsjf.color }}>{wsjf.label}</strong>;
        },
      },
      {
        title: 'Principal',
        field: 'principalHours',
        type: 'numeric' as const,
        render: (row: TechDebtIssue) => `${formatNumber(row.principalHours)}h`,
      },
      {
        title: 'Interest',
        field: 'monthlyInterestHours',
        type: 'numeric' as const,
        render: (row: TechDebtIssue) => `${formatNumber(row.monthlyInterestHours)}h`,
      },
    ],
    [],
  );

  useEffect(() => {
    let mounted = true;

    const loadIssues = async () => {
      if (mounted) {
        setIsLoading(true);
        setLoadError(undefined);
      }

      try {
        const payloadItems = await techDebtApi.listIssuesByEntityRef(
          inferredEntityRef,
        );
        if (mounted) {
          setIssues(payloadItems);
        }
      } catch {
        if (mounted) {
          setIssues([]);
          setLoadError('Failed to load tech debt issues');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadIssues();

    return () => {
      mounted = false;
    };
  }, [inferredEntityRef, isOpen, techDebtApi]);

  return (
    <>
      {showRegisterButton && (
        <Button
          variant="contained"
          color="primary"
          style={{ marginBottom: '16px' }}
          onClick={event => {
            event.stopPropagation();
            setIsOpen(true);
          }}
        >
          Register New Tech Debt
        </Button>
      )}

      {loadError && <Typography color="error">{loadError}</Typography>}

      <Table<TechDebtIssue>
        title="Tech Debt Issues"
        columns={columns}
        data={issues}
        isLoading={isLoading}
        options={{
          paging: true,
          search: true,
          pageSize: 10,
          padding: 'dense',
          toolbar: true,
        }}
        emptyContent="No tech debt issues found for this entity"

      />

      <TechDebtRegistrationDrawer
        open={isOpen}
        context={context}
        initialEntityRef={inferredEntityRef}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};

export const TechDebtComponentEntityContent = () => (
  <TechDebtEntityContent context="kind: Component" />
);

export const TechDebtSystemEntityContent = () => (
  <TechDebtEntityContent context="kind: System" />
);

export const TechDebtEnterpriseLayerEntityContent = () => (
  <TechDebtEntityContent
    context="kind: Domain, type: EnterpriseLayer"
    showRegisterButton={false}
  />
);

export const TechDebtFunctionalDomainEntityContent = () => (
  <TechDebtEntityContent
    context="kind: Domain, type: FunctionalDomain"
    showRegisterButton={false}
  />
);

